import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Save, PieChart as PieIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const CLASSES = ['stocks', 'bonds', 'crypto', 'cash', 'real_estate', 'other'];

const WealthAllocation = () => {
  const [targets, setTargets] = useState([]);
  const [summary, setSummary] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [t, s] = await Promise.all([
        axios.get(`${API}/wealth/allocation/targets`),
        axios.get(`${API}/wealth/summary`),
      ]);
      const existing = {};
      (t.data.targets || []).forEach((x) => { existing[x.asset_class] = x.target_pct; });
      setTargets(CLASSES.map((c) => ({ asset_class: c, target_pct: existing[c] ?? 0 })));
      setSummary(s.data);
    } catch (e) {
      toast.error('Failed to load allocation');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setPct = (cls, val) => {
    setTargets((prev) => prev.map((t) => t.asset_class === cls ? { ...t, target_pct: parseFloat(val) || 0 } : t));
  };

  const total = targets.reduce((s, t) => s + (t.target_pct || 0), 0);
  const totalOk = Math.abs(total - 100) < 0.5;

  const save = async () => {
    if (!totalOk) {
      toast.error(`Targets must sum to 100 (currently ${total.toFixed(1)})`);
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/wealth/allocation/targets`, { targets });
      toast.success('Allocation targets saved');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const liveLookup = {};
  (summary?.allocation || []).forEach((a) => { liveLookup[a.asset_class] = a; });

  return (
    <div className="p-8 md:p-12" data-testid="wealth-allocation-page">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <div className="mb-8 flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-4xl font-chivo font-bold text-slate-900 tracking-tight">Target allocation</h1>
            <p className="text-slate-600 mt-1 font-ibm">Set your ideal mix across asset classes. Drift shows how far you are from target.</p>
          </div>
          <Button
            onClick={save}
            disabled={!totalOk || saving}
            className="bg-slate-900 text-white rounded-sm hover:bg-slate-800 disabled:opacity-50"
            data-testid="save-targets-btn"
          >
            <Save size={14} className="mr-2" />
            {saving ? 'Saving…' : 'Save targets'}
          </Button>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm p-6">
          <table className="w-full text-sm font-ibm" data-testid="allocation-targets-table">
            <thead>
              <tr className="text-xs text-slate-500 uppercase tracking-widest border-b border-slate-200">
                <th className="text-left py-3 pr-4">Asset class</th>
                <th className="text-right py-3 pr-4 w-32">Target %</th>
                <th className="text-right py-3 pr-4 w-32">Current %</th>
                <th className="text-right py-3 w-32">Drift</th>
              </tr>
            </thead>
            <tbody>
              {targets.map((t) => {
                const live = liveLookup[t.asset_class];
                const drift = live ? (live.pct - t.target_pct) : -t.target_pct;
                const driftColor = Math.abs(drift) < 2 ? 'text-emerald-600' : drift > 0 ? 'text-amber-600' : 'text-red-600';
                return (
                  <tr key={t.asset_class} className="border-b border-slate-100 last:border-0" data-testid={`target-row-${t.asset_class}`}>
                    <td className="py-3 pr-4 capitalize text-slate-900 font-medium">{t.asset_class.replace('_', ' ')}</td>
                    <td className="py-3 pr-4">
                      <Input
                        type="number"
                        step="1"
                        value={t.target_pct}
                        onChange={(e) => setPct(t.asset_class, e.target.value)}
                        className="text-right"
                        data-testid={`target-input-${t.asset_class}`}
                      />
                    </td>
                    <td className="py-3 pr-4 text-right tabular-nums text-slate-700" data-testid={`current-pct-${t.asset_class}`}>
                      {live ? `${live.pct.toFixed(1)}%` : '—'}
                    </td>
                    <td className={`py-3 text-right tabular-nums font-semibold ${driftColor}`} data-testid={`drift-${t.asset_class}`}>
                      {drift > 0 ? '+' : ''}{drift.toFixed(1)}pp
                    </td>
                  </tr>
                );
              })}
              <tr className="border-t-2 border-slate-900">
                <td className="py-3 font-chivo font-bold uppercase tracking-widest text-xs text-slate-900">Total</td>
                <td className={`py-3 pr-4 text-right tabular-nums font-chivo font-bold ${totalOk ? 'text-emerald-600' : 'text-red-600'}`} data-testid="targets-total">
                  {total.toFixed(1)}%
                </td>
                <td></td>
                <td></td>
              </tr>
            </tbody>
          </table>
          {!totalOk && (
            <p className="mt-4 text-sm font-ibm text-red-600" data-testid="targets-total-error">
              Targets must sum to 100%. Current total: {total.toFixed(1)}%.
            </p>
          )}
        </div>

        <div className="mt-4 bg-slate-50 border border-slate-200 rounded-sm p-6 text-sm font-ibm text-slate-700 flex gap-3 items-start">
          <PieIcon size={18} className="text-slate-600 mt-0.5 flex-shrink-0" />
          <p>Rebalance suggestion: aim to pull overweight classes (positive drift) back toward target and top up underweight classes (negative drift). The AI insights on the Portfolio page translate this into concrete action items for your current holdings.</p>
        </div>
      </motion.div>
    </div>
  );
};

export default WealthAllocation;
